/**
 * The ONLY producer of `addTab`'s analytics pageview arguments (Azure Storage
 * spec §6.1, CLAUDE.md no-PII rule). `analyticsIpc.ts` forwards the title
 * untouched into the outbound Umami payload, so the title must never carry a
 * user value: every tab type answers a STATIC label describing the surface,
 * never the tab's own title (which is a `schema.table`, a container path, a
 * file name or a user-typed query name).
 *
 * Product ruling P1 (a), 2026-09-20 (spec §18): this applies to EVERY tab type
 * and every engine — the pre-existing `schema.table` pageview title for
 * `table-explorer` tabs is gone. The function therefore takes no title at all,
 * so a leak is structurally impossible rather than merely avoided.
 */
import type { WorkspaceType } from '../types/workspaceTypes';

/** Static, low-cardinality surface label per workspace type. Exhaustive: a new `WorkspaceType` member fails the `never` guard until it gets a label. */
function pageviewTitleFor(type: WorkspaceType): string {
  switch (type) {
    case 'query': return 'Query Editor';
    case 'table-editor': return 'Table Editor';
    case 'table-explorer': return 'Table Explorer';
    case 'plan': return 'Execution Plan';
    case 'table-designer': return 'Table Designer';
    case 'security': return 'Security';
    case 'schema-compare': return 'Schema Compare';
    case 'data-compare': return 'Data Compare';
    case 'dependency-viewer': return 'Dependency Viewer';
    case 'schema-overview': return 'Schema Overview';
    case 'data-profile': return 'Data Profile';
    case 'start-page': return 'Start Page';
    case 'notebook': return 'Notebook';
    case 'session-browser': return 'Session Browser';
    case 'tablespace-manager': return 'Tablespace Manager';
    case 'oracle-object-detail': return 'Object Detail';
    case 'data-pump': return 'Data Pump';
    case 'instance-monitor': return 'Instance Monitor';
    case 'filegroup-manager': return 'Filegroup Manager';
    case 'pl-sql-debugger': return 'PL/SQL Debugger';
    case 'job-scheduler': return 'Job Scheduler';
    case 'plsql-editor': return 'PL/SQL Editor';
    case 'package-explorer': return 'Package Explorer';
    case 'references-tab': return 'References';
    case 'database-blueprint': return 'Database Blueprint';
    case 'cross-engine-migrate': return 'Cross-Engine Migrate';
    case 'dashboard': return 'Dashboard';
    case 'spatial-explorer': return 'Spatial Explorer';
    case 'data-import': return 'Data Import';
    case 'deadlock-analyzer': return 'Deadlock Analyzer';
    case 'query-store': return 'Query Store';
    case 'storage-explorer': return 'Storage browser';
    default: {
      // Exhaustiveness guard: unreachable for a well-typed `WorkspaceType`.
      const unhandled: never = type;
      void unhandled;
      return 'Workspace';
    }
  }
}

export const STORAGE_EXPLORER_PAGEVIEW = { url: '/storage-explorer', title: 'Jam SQL - Storage browser' } as const;

export function pageviewForTab(type: WorkspaceType): { url: string; title: string } {
  return { url: `/${type}`, title: `Jam SQL - ${pageviewTitleFor(type)}` };
}
